import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Settings,
  Trash2,
  Copy,
  RefreshCw,
  Grid,
  Upload,
  LayoutGrid,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  Filter,
  Archive,
  RotateCcw,
  Database,
  Loader2,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StateBadge } from "@/components/portal/tables/state-machine/StateBadge";
import { ExportToExcelButton } from "@/components/ui/export-to-excel-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { myappApi } from "@/services/api/myapp";
import { useVirtualRefresh } from "@/hooks/useVirtualRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { formatCellValue } from "@/lib/myapp/format-cell-value";
import { canAddRecords } from "@/lib/myapp/tableCapabilities";
import { getSharedFkCollisionColumns } from "@/lib/myapp/shared-fk-collision";
import { resolveListDisplayValue } from "@/lib/myapp/link-display";
import { effectiveFieldTypeCode } from "@/lib/myapp/field-type";
import { ExportOptionsDialog } from "@/components/portal/tables/ExportOptionsDialog";
import { ImportDialog } from "@/components/portal/tables/ImportDialog";
import { ImportVersionsPanel } from "@/components/portal/tables/ImportVersionsPanel";
import { VersionMilestonePanel } from "@/components/portal/tables/VersionMilestonePanel";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import { useTableMetadata } from "@/hooks/useTableMetadata";
import {
  useConnectorAttachmentBinding,
  useConnectorAttachmentCounts,
  useConnectorAttachmentSource,
} from "@/hooks/useConnectorAttachments";
import { AttachmentListPopover } from "@/components/portal/tables/AttachmentListPopover";
import {
  ConnectorFilesCell,
  FilesErrorMarker,
} from "@/components/portal/tables/ConnectorFilesCell";
import { FILES_BINDING_ERROR, connectorBindingState } from "@/lib/myapp/connector-binding-state";
import type { FormField, FormRecord, ColumnNamingPreference } from "@/types/myapp";
import { useAddinHandoff } from "./useAddinHandoff";

/** dp's connector-attachment list caps `source_key` at this length. */
const MAX_CONNECTOR_SOURCE_KEY_LENGTH = 100;

export const Route = createFileRoute("/portal/myapp/$tableId/")({
  component: FormDataPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { jobId?: string; orgId?: string; refreshJobId?: string } => ({
    // Optional jobId carried in from the MyApp Excel Add-in handoff:
    // /portal/myapp/{tableId}?jobId={X} resumes ImportDialog from an
    // already-uploaded preview without re-uploading.
    jobId: typeof search.jobId === "string" ? search.jobId : undefined,
    // Optional orgId — also from the addin handoff. PortalDataProvider
    // restores whichever organisation localStorage last remembered, so
    // a multi-org user clicking the addin's deep-link could otherwise
    // land in the wrong tenant. When supplied, FormDataPage switches
    // the active organisation to match before resolving the table.
    orgId: typeof search.orgId === "string" ? search.orgId : undefined,
    // Optional refreshJobId — from publish (dp #2213). A virtual table's first
    // data load is queued on the MyApp worker; this page watches that job
    // exactly as it watches a Refresh Data click.
    refreshJobId:
      typeof search.refreshJobId === "string" ? search.refreshJobId : undefined,
  }),
});

// ExpandableText component for truncating long text with expand/collapse
const MAX_TEXT_LENGTH = 100;

function ExpandableText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!text || text.length <= MAX_TEXT_LENGTH) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {expanded ? text : text.slice(0, MAX_TEXT_LENGTH) + "…"}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className="ml-1 text-xs text-primary hover:underline font-medium"
      >
        {expanded ? "less" : "more"}
      </button>
    </span>
  );
}

// Resizable column header — wider hit area (12px) with thin visual indicator on hover
function ResizableHeader({
  fieldId,
  width,
  onResize,
  onClick,
  children,
}: {
  fieldId: string;
  width: number | undefined;
  onResize: (id: string, width: number) => void;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const headerRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX.current;
      const newWidth = Math.max(60, startWidth.current + delta);
      onResize(fieldId, newWidth);
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, fieldId, onResize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width ?? headerRef.current?.offsetWidth ?? 150;
  };

  return (
    <TableHead
      ref={headerRef}
      style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : undefined}
      className="relative cursor-pointer hover:bg-muted/50 group/resize"
      onClick={onClick}
    >
      {children}
      {/* Wide hit area (12px), thin visual bar (2px) appears on hover */}
      <div
        className="absolute right-0 top-0 h-full w-3 cursor-col-resize flex justify-center"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`h-full w-0.5 ${isResizing ? "bg-primary" : "bg-transparent group-hover/resize:bg-border"}`} />
      </div>
    </TableHead>
  );
}

function FormDataPage() {
  const { tableId } = Route.useParams();
  const {
    jobId: resumeImportJobId,
    orgId: handoffOrgId,
    refreshJobId: publishRefreshJobId,
  } = Route.useSearch();
  const navigate = useNavigate();
  const { token, session } = useAuth();
  // Mirror the live token in a ref so the records-list fetch (and the child-count
  // fetch) aren't recreated/re-run when a silent JWT refresh rotates `token`.
  // Were `token` in their deps, a refresh would re-fetch the list mid-browse and
  // flash the skeleton. Same idiom as the submit forms + grid (Layer 1).
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const {
    organisations,
    selectedOrganisation,
    setSelectedOrganisationId,
  } = usePortalData();
  const isPlatformAdmin = session?.user?.is_admin === true;
  const formId = tableId;

  // Addin deep-link handoff: switch tenants if `?orgId=` is in the URL
  // and the user belongs to that org, then strip the param. See
  // useAddinHandoff for full behaviour + toast feedback.
  //
  // `silentSuccess` suppresses the org-switch success toast when a
  // jobId is also present — ImportDialog's "Continued from the Excel
  // Add-in" banner already explains the handoff context, and a toast
  // landing on top of the auto-opening dialog reads as noise.
  useAddinHandoff({
    handoffOrgId,
    organisations,
    selectedOrganisation,
    setSelectedOrganisationId,
    navigate,
    silentSuccess: !!resumeImportJobId,
  });

  // Defer the table-metadata fetch when an addin handoff is asking
  // us to switch tenants but the switch hasn't landed yet — otherwise
  // it fires against the user's previous (stale) org and 404s, then
  // doesn't re-fire when the auto-switch effect above eventually
  // settles. The gate fires only when ALL of:
  //   - the URL carried ?orgId=
  //   - PortalDataProvider has loaded the org list
  //   - the selected org doesn't yet match the handoff orgId
  // Once the switch lands the dep changes and the hook re-runs.
  const handoffPending = Boolean(
    handoffOrgId &&
      organisations.length > 0 &&
      selectedOrganisation?.organisationId !== handoffOrgId,
  );

  // Shared metadata loader — TanStack-Query-backed when the
  // `myapp.cache.tanstack` flag is on (revisits within 5 min are
  // cache-served), or imperative parallel fetch when off. Both
  // branches return the same shape so the rest of this component
  // does not branch on the flag.
  const {
    form,
    tableStates,
    loading,
    error,
    refetch: fetchForm,
  } = useTableMetadata(formId, { enabled: !handoffPending });
  const [records, setRecords] = useState<FormRecord[]>([]);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const hasStateMachine = tableStates.length > 0;
  const [total, setTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // Pagination
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Dual horizontal scrollbar refs
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const syncing = useRef(false);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteRefs, setDeleteRefs] = useState<{ table_name: string; record_count: number }[]>([]);
  const [checkingRefs, setCheckingRefs] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [childFormCount, setChildFormCount] = useState(0);

  // Sort state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDesc, setSortDesc] = useState(false);

  // Global search state (debounced for server-side search)
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Auto-open the import dialog when the URL carries ?jobId=X (e.g.
  // from the MyApp Excel Add-in handing off an in-flight upload).
  //
  // After consuming the jobId we strip it from the URL: the upload
  // preview cache TTL is short (~10 minutes), so leaving it in the
  // URL means a refresh would try to resume a job that's already
  // expired and fail confusingly.
  //
  // The captured-jobId state survives the URL strip so ImportDialog
  // still receives `initialJobId` on its first open. On dialog close
  // we clear the state so re-opening (e.g. user clicks Import again)
  // goes through the normal upload flow rather than re-resuming the
  // same expired job.
  const [pendingResumeJobId, setPendingResumeJobId] = useState<
    string | undefined
  >(resumeImportJobId);
  useEffect(() => {
    if (!resumeImportJobId) return;
    setPendingResumeJobId(resumeImportJobId);
    setShowImportDialog(true);
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, jobId: undefined }),
      replace: true,
    });
  }, [resumeImportJobId, navigate]);

  const handleImportDialogChange = useCallback((open: boolean) => {
    setShowImportDialog(open);
    if (!open) {
      // Once the user dismisses the dialog, the resumed jobId is no
      // longer useful — clear it so the next manual open is a clean
      // upload flow, not a re-resumption of a now-expired job.
      setPendingResumeJobId(undefined);
    }
  }, []);

  // Virtual table refresh
  const {
    refreshing: virtualRefreshing,
    status: virtualRefreshStatus,
    triggerRefresh: handleVirtualRefresh,
    watchJob: watchVirtualRefreshJob,
  } = useVirtualRefresh({
    token,
    formId: String(formId),
    onSuccess: async () => {
      await fetchRecords();
      // Invalidate + refetch the cached metadata so the virtual-table
      // refresh's structural changes flow through.
      await fetchForm();
    },
  });

  // dp #2213: publish queued this virtual table's first load on the worker and
  // passed its job id here. Watch it once, then drop the param so a reload or
  // back-navigation doesn't re-watch a finished job.
  const watchedPublishJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (!publishRefreshJobId || !token) return;
    if (watchedPublishJobRef.current === publishRefreshJobId) return;
    watchedPublishJobRef.current = publishRefreshJobId;
    navigate({
      to: "/portal/myapp/$tableId",
      params: { tableId },
      search: (prev: Record<string, unknown>) => ({ ...prev, refreshJobId: undefined }),
      replace: true,
    });
    void watchVirtualRefreshJob(publishRefreshJobId);
  }, [publishRefreshJobId, token, tableId, navigate, watchVirtualRefreshJob]);

  // Purge state
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purging, setPurging] = useState(false);
  const [checkingPurge, setCheckingPurge] = useState(false);
  const [purgeBlockedRefs, setPurgeBlockedRefs] = useState<{table_name: string; record_count: number}[]>([]);

  // Multi-select state for bulk delete
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // Column filtering state
  type ColumnFilter = {
    value: string;
    op?: "contains" | "starts_with" | "ends_with" | "equals";
    value2?: string;
  };
  const [columnFilters, setColumnFilters] = useState<Record<string, ColumnFilter>>({});
  const [debouncedFilters, setDebouncedFilters] = useState<Record<string, ColumnFilter>>({});
  const [showFilters, setShowFilters] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SysStatus visibility toggles
  const [includeArchived, setIncludeArchived] = useState(false);

  // Column resize state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const handleColumnResize = useCallback((fieldId: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [fieldId]: width }));
  }, []);

  // Restore/Purge archive state (for latest tables)
  const [showRestoreArchiveDialog, setShowRestoreArchiveDialog] = useState(false);
  const [showPurgeArchiveDialog, setShowPurgeArchiveDialog] = useState(false);
  const [archiveActioning, setArchiveActioning] = useState(false);

  // Tier 4: Object-level permissions (shared hook — blocks render until resolved)
  const { permissions, loading: permsLoading, denied: permissionDenied } = useMyAppPermissions(formId);

  // Connector attachments: a table bound to a source-system record kind shows
  // a Files column — the standard read-only AttachmentListPopover (paperclip +
  // "N attachments"), fed from the connector-attachments API (D27). Held until
  // the user may view the published table — the page renders nothing
  // otherwise. `null` = no binding. `failed` = the call failed with no
  // binding to use (a failed background refetch keeps the earlier one).
  const {
    binding: connectorBinding,
    pending: connectorBindingPending,
    failed: connectorBindingFailed,
  } = connectorBindingState(
    useConnectorAttachmentBinding(formId, {
      enabled: !!permissions?.can_view && !!form?.is_published,
    }),
  );
  // A virtual table with a `Files` field of type connector_files holds the
  // count in its own column: the field's cell renders the popover from the row
  // value and the header sorts like any column, so the separate Files column
  // and its per-page /counts request are skipped (the count shown is the count
  // sorted on).
  const hasFilesField = !!form?.fields.some(
    (f) => !f.is_hidden && f.field_type?.code === "connector_files",
  );
  // A failed binding call (anything but the 404 "no binding") must not just
  // hide the column: whether the table HAS files is unknown, so the Files
  // column is shown with the same error marker as a failed counts call.
  const showFilesColumn = !hasFilesField && (connectorBinding !== null || connectorBindingFailed);
  const connectorAttachmentSource = useConnectorAttachmentSource(formId);
  // The row's source-record id, read from the record payload. The list API
  // returns visible fields plus the primary key only, so the bound column
  // must be visible, or be the primary key, to yield a key. Compared
  // server-side as an exact string, so it is sent as `String(value)`.
  const getSourceKey = (record: FormRecord): string | null => {
    if (!connectorBinding) return null;
    const value = (record as Record<string, unknown>)[connectorBinding.source_key_column_name];
    if (value === null || value === undefined || value === "") return null;
    const key = String(value);
    // dp's file list caps `source_key` at 100 characters (`max_length=100`):
    // a longer key could be counted but never opened, so it is not asked for.
    return key.length > MAX_CONNECTOR_SOURCE_KEY_LENGTH ? null : key;
  };
  const connectorSourceKeys = connectorBinding
    ? records.map(getSourceKey).filter((k): k is string => k !== null)
    : [];
  // A binding whose column is absent from the payload (hidden, not the PK)
  // shows a Files column with no counts and no error — say why, once.
  const warnedNoSourceKey = useRef<string | null>(null);
  const bindingYieldsNoKeys =
    connectorBinding !== null && records.length > 0 && connectorSourceKeys.length === 0;
  useEffect(() => {
    if (!bindingYieldsNoKeys || !connectorBinding) return;
    const warnKey = `${formId}:${connectorBinding.source_key_column_name}`;
    if (warnedNoSourceKey.current === warnKey) return;
    warnedNoSourceKey.current = warnKey;
    console.warn(
      `Connector attachments: table ${formId} is bound on column ` +
        `"${connectorBinding.source_key_column_name}", but no row on this page has a value ` +
        "for it (or only values over 100 characters). The column must be visible, or be the primary key.",
    );
  }, [bindingYieldsNoKeys, connectorBinding, formId]);
  // ONE counts request per page of records; none without a binding, and none
  // when a connector_files field already carries the count.
  const { data: connectorCounts, isError: connectorCountsFailed } = useConnectorAttachmentCounts(
    formId,
    connectorSourceKeys,
    connectorBinding !== null && !hasFilesField,
  );
  const filesColumnError = connectorBindingFailed
    ? FILES_BINDING_ERROR
    : connectorCountsFailed
      ? "File counts could not be loaded"
      : null;

  // (fetchForm + its triggering useEffect are owned by
  // `useTableMetadata` above. The retry button and virtual-refresh
  // success handler call the hook's `refetch` aliased as `fetchForm`.)

  // Build API-compatible filters from column filter state
  const buildApiFilters = useCallback((filters: Record<string, ColumnFilter>): Record<string, unknown> => {
    const apiFilters: Record<string, unknown> = {};
    // System column names start with "Sys" — always text-based filters
    const sysPrefix = "Sys";
    for (const [colName, filter] of Object.entries(filters)) {
      if (!filter.value && !filter.value2) continue;

      // System columns are always text-based
      if (colName.startsWith(sysPrefix)) {
        if (filter.value) apiFilters[colName] = filter.value;
        continue;
      }

      // Find the field to determine type (a lookup reports its resolved type).
      const field = form?.fields.find((f) => f.column_name === colName);
      const code = (field && effectiveFieldTypeCode(field)) || "text";

      if (["int", "bigint", "decimal", "money", "percentage", "calculation"].includes(code)) {
        // Numeric range filter
        const rangeFilter: Record<string, unknown> = {};
        if (filter.value) rangeFilter.min = Number(filter.value);
        if (filter.value2) rangeFilter.max = Number(filter.value2);
        if (Object.keys(rangeFilter).length > 0) apiFilters[colName] = rangeFilter;
      } else if (["date", "datetime"].includes(code)) {
        // Date range filter
        const rangeFilter: Record<string, unknown> = {};
        if (filter.value) rangeFilter.from = filter.value;
        if (filter.value2) rangeFilter.to = filter.value2;
        if (Object.keys(rangeFilter).length > 0) apiFilters[colName] = rangeFilter;
      } else if (code === "bit") {
        // Boolean filter
        if (filter.value) apiFilters[colName] = filter.value;
      } else {
        // Text-like filter
        if (filter.value) apiFilters[colName] = filter.value;
      }
    }
    return apiFilters;
  }, [form?.fields]);

  const fetchRecords = useCallback(async () => {
    if (!form?.is_published || !tokenRef.current) return;

    try {
      setRecordsLoading(true);
      const apiFilters = buildApiFilters(debouncedFilters);
      const result = await myappApi.records.list(tokenRef.current, formId, {
        limit,
        offset,
        orderBy: sortColumn || undefined,
        orderDesc: sortColumn ? sortDesc : undefined,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
        search: debouncedSearch || undefined,
        includeArchived: includeArchived || undefined,
      });
      setRecords(result.items);
      setTotal(result.total);
    } catch (err) {
      console.error("Failed to load records:", err);
    } finally {
      setRecordsLoading(false);
    }
    // `token` read via tokenRef.current; omitted from deps so a silent JWT
    // refresh doesn't re-run this and flash the records skeleton mid-browse.

  }, [formId, form?.is_published, limit, offset, sortColumn, sortDesc, debouncedFilters, debouncedSearch, buildApiFilters, includeArchived]);

  // (Initial fetch of `form` + `tableStates` is handled inside
  // `useTableMetadata`. The handoff guard is applied via the
  // `enabled` option on the hook above. Nothing to do here.)

  useEffect(() => {
    if (form?.is_published) {
      fetchRecords();
    }
  }, [form?.is_published, fetchRecords]);

  // Fetch child form count for advanced tables
  useEffect(() => {
    if (form?.is_header_table && tokenRef.current) {
      myappApi.tables.getChildren(tokenRef.current, formId)
        .then((children) => setChildFormCount(children.length))
        .catch(() => setChildFormCount(0));
    } else {
      setChildFormCount(0);
    }
    // `token` read via tokenRef.current; omitted so a JWT refresh doesn't re-run this.

  }, [form?.is_header_table, formId]);

  // Check references when a record is about to be deleted
  useEffect(() => {
    if (!deleteId || !token) {
      setDeleteRefs([]);
      return;
    }
    setCheckingRefs(true);
    myappApi.records.checkReferences(token, formId, deleteId)
      .then((refs) => setDeleteRefs(refs))
      .catch(() => setDeleteRefs([]))
      .finally(() => setCheckingRefs(false));
  }, [deleteId, formId, token]);

  const handleDelete = async () => {
    if (!deleteId || !form || !token) return;

    try {
      setDeleting(true);
      if (form.is_header_table) {
        await myappApi.composite.delete(token, formId, deleteId);
      } else {
        await myappApi.records.delete(token, formId, deleteId);
      }
      setDeleteId(null);
      await fetchRecords();
    } catch (err) {
      console.error("Failed to delete record:", err);
      alert(err instanceof Error ? err.message : "Failed to delete record");
    } finally {
      setDeleting(false);
    }
  };

  // --- Archive handlers (latest tables) ---

  const handleRestoreArchive = async () => {
    if (!token) return;
    try {
      setArchiveActioning(true);
      await myappApi.records.restoreArchived(token, formId);
      toast.success("Archived data restored — current and previous data have been swapped.");
      setShowRestoreArchiveDialog(false);
      await fetchRecords();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore archived data");
    } finally {
      setArchiveActioning(false);
    }
  };

  const handlePurgeArchive = async () => {
    if (!token) return;
    try {
      setArchiveActioning(true);
      const result = await myappApi.records.purgeArchived(token, formId);
      toast.success(`Purged ${result.purged_count} archived rows.`);
      setShowPurgeArchiveDialog(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to purge archived data");
    } finally {
      setArchiveActioning(false);
    }
  };

  // Open the export dialog
  const handleExportClick = () => {
    setShowExportDialog(true);
  };


  // Perform the actual export based on user preferences (async server-side)
  const handleExportToExcel = async (
    columnNaming: ColumnNamingPreference,
    includeArchivedExport: boolean,
    ignoreFilters: boolean,
    linkValueSource: "capture" | "current",
  ) => {
    if (!form || !token) return;

    try {
      setExporting(true);
      setShowExportDialog(false);

      const loadingToastId = toast.loading("Starting export...");

      const isComposite = form.is_header_table && childFormCount > 0;

      // Inherit the grid's active filter / search unless the user
      // explicitly opted out via the "Export full table" checkbox.
      // Backend's body is optional - undefined / empty short-circuits
      // it to the "export everything" path.
      const apiFilters = ignoreFilters ? undefined : buildApiFilters(debouncedFilters);
      const searchPayload = ignoreFilters ? undefined : (debouncedSearch || undefined);

      // 1. Start async export on server
      const { job_id } = await myappApi.import.startExport(
        token,
        formId,
        columnNaming,
        isComposite,
        includeArchivedExport,
        apiFilters,
        searchPayload,
        linkValueSource,
      );

      toast.loading("Generating export on server...", { id: loadingToastId });

      // 2. Poll for completion (every 2s, 5-minute timeout)
      // Transient errors (e.g. cold-start 502s) are retried up to MAX_TRANSIENT_ERRORS
      // times before giving up, so a slow worker spin-up doesn't surface as a failure.
      const POLL_INTERVAL = 2000;
      const MAX_POLL_TIME = 5 * 60 * 1000;
      const MAX_TRANSIENT_ERRORS = 20; // 20 × 2s = 40s tolerance — covers KEDA cold-start (~38s observed)
      const startTime = Date.now();

      let completed = false;
      let fileName: string | undefined;
      let transientErrors = 0;

      while (Date.now() - startTime < MAX_POLL_TIME) {
        try {
          const status = await myappApi.import.getExportStatus(token, formId, job_id);
          transientErrors = 0;

          if (status.status === "completed") {
            completed = true;
            fileName = status.file_name ?? undefined;
            if (status.row_count != null) {
              toast.loading(`Export ready — ${status.row_count} rows. Downloading...`, {
                id: loadingToastId,
              });
            }
            break;
          }

          if (status.status === "failed") {
            toast.error(status.error || "Export failed on server", { id: loadingToastId });
            return;
          }
        } catch (pollErr) {
          transientErrors++;
          if (transientErrors >= MAX_TRANSIENT_ERRORS) {
            throw pollErr;
          }
          // Transient error (e.g. cold-start 502, brief Redis miss) — keep polling
        }

        // Still processing — wait before next poll
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
      }

      if (!completed) {
        toast.error("Export timed out. Please try again.", { id: loadingToastId });
        return;
      }

      // 3. Download the generated file
      await myappApi.import.downloadExport(token, formId, job_id, fileName);

      toast.success(`Exported to Excel successfully`, { id: loadingToastId });
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(err instanceof Error ? err.message : "Failed to export to Excel");
    } finally {
      setExporting(false);
    }
  };

  // Numeric PK for selection/checkbox operations (regular tables only)
  const getPkValue = (record: FormRecord): number | null => {
    if (!form) return null;
    const pk = record[form.primary_key_column];
    return typeof pk === "number" ? pk : null;
  };

  // Raw PK value (string or number) for React keys and display — handles
  // virtual tables whose unique key may be NVARCHAR (e.g., "BPA", "00203")
  const getRawPkValue = (record: FormRecord): string | number | null => {
    if (!form) return null;
    const pk = record[form.primary_key_column];
    if (typeof pk === "number" || typeof pk === "string") return pk;
    return null;
  };

  // Link columns sharing one physical FK column (shared-FK collision). For
  // these, the {col}_display JOIN resolves through the shared last-write-wins
  // FK and is unreliable, so the list must read each field's own stored
  // snapshot ([col]) instead. Empty set for non-collided tables (no change).
  const sharedFkCollisionCols = useMemo(
    () => getSharedFkCollisionColumns(form?.fields ?? []),
    [form?.fields],
  );

  // Get the display value for a field. For link fields this prefers the live
  // {col}_display JOIN, except for shared-FK collision columns where it reads
  // the field's own snapshot instead (the JOIN is unreliable there). See
  // resolveListDisplayValue for the full rule.
  const getFieldValue = (record: FormRecord, field: FormField): unknown =>
    resolveListDisplayValue(record, field, sharedFkCollisionCols);

  // Handle column header click for sorting
  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      // Toggle direction if same column
      setSortDesc(!sortDesc);
    } else {
      // New column - default to ascending
      setSortColumn(columnName);
      setSortDesc(false);
    }
    setOffset(0); // Reset to first page when sorting changes
  };

  // Column filter helpers
  const updateColumnFilter = (column: string, update: Partial<ColumnFilter>) => {
    const existing = columnFilters[column] || { value: "" };
    const merged = { ...existing, ...update };
    const next = { ...columnFilters, [column]: merged };
    setColumnFilters(next);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(next);
      setOffset(0);
    }, 500);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setDebouncedFilters({});
    setOffset(0);
  };

  const handleSearchChange = (value: string) => {
    setFilterText(value);
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
      setOffset(0);
    }, 400);
  };

  const hasActiveFilters = Object.values(columnFilters).some(
    (f) => f.value || f.value2
  );

  // Row selection helpers
  const toggleRowSelect = (pk: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pk)) next.delete(pk);
      else next.add(pk);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecords.length) {
      setSelectedIds(new Set());
    } else {
      const allPks = filteredRecords
        .map((r) => getPkValue(r))
        .filter((pk): pk is number => pk !== null);
      setSelectedIds(new Set(allPks));
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || !token) return;
    try {
      setBulkDeleting(true);
      await myappApi.records.deleteBatch(token, formId, Array.from(selectedIds));
      toast.success(`Deleted ${selectedIds.size} record${selectedIds.size !== 1 ? "s" : ""}`);
      setSelectedIds(new Set());
      setShowBulkDeleteDialog(false);
      await fetchRecords();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete records");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Purge handler
  const handlePurge = async () => {
    if (!form || !token || purgeConfirmText !== form.table_name) return;
    try {
      setPurging(true);
      const result = await myappApi.records.purge(token, formId, purgeConfirmText);
      toast.success(`Purged ${result.deleted_count} records${result.child_tables_purged > 0 ? ` and ${result.child_tables_purged} child table(s)` : ""}`);
      setShowPurgeDialog(false);
      setPurgeConfirmText("");
      await fetchRecords();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to purge data");
    } finally {
      setPurging(false);
    }
  };

  // Get visible fields (not hidden) — memoised so downstream useEffect deps are stable.
  // Excludes the PK field when it overlaps with a user-defined field (virtual tables
  // with a unique key), since the PK column is rendered separately.
  const visibleFields = useMemo(
    () =>
      form?.fields.filter(
        (f) => !f.is_hidden && f.column_name !== form.primary_key_column,
      ) ?? [],
    [form?.fields, form?.primary_key_column],
  );

  // System metadata columns shown at the end of the grid
  const systemColumns = (() => {
    if (!form) return [];
    const cols: { key: string; label: string }[] = [];
    const tt = form.table_type || "simple";
    const vm = form.version_mode || "filename";
    const am = form.area_mode || "none";
    // Virtual tables have no system columns beyond SysStatus
    if (tt === "virtual") return cols;
    // SysImportFileName is always present (populated during every import)
    cols.push({ key: "SysImportFileName", label: "Import Filename" });
    if (tt === "versioned" && (vm === "version_only" || vm === "version_and_milestone")) {
      cols.push({ key: "SysVersionName", label: "Version" });
      if (vm === "version_and_milestone")
        cols.push({ key: "SysMilestoneName", label: "Milestone" });
    }
    if (am !== "none") {
      cols.push({ key: "SysAreaName", label: "Area" });
      if (am === "areas_and_subareas")
        cols.push({ key: "SysSubAreaName", label: "Sub-area" });
    }
    // Workflow-state column — only on top-level Simple/Advanced tables.
    if (
      (tt === "simple" || tt === "advanced") &&
      !form.parent_table_id &&
      hasStateMachine
    ) {
      cols.push({ key: "SysCurrentStateName", label: "State" });
    }
    // Always-on audit columns. Selected by the data API for every record;
    // we just surface them in the grid for sortability + quick scanning.
    cols.push({ key: "SysCreatedAtUtc", label: "Created" });
    cols.push({ key: "SysUpdatedAtUtc", label: "Updated" });
    return cols;
  })();

  // Records are now filtered server-side via the `search` query param.
  // No client-side filtering needed — `records` already contains the
  // filtered page returned from the API.
  const filteredRecords = records;

  // Pagination calculations
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const goToPage = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handlePageSizeChange = useCallback((size: number) => {
    setLimit(size);
    setOffset(0);
  }, []);

  // Dual scrollbar sync — keep top and bottom scrollbars in lockstep
  const syncScrollFromTop = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    if (topScrollRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    syncing.current = false;
  }, []);

  const syncScrollFromTable = useCallback(() => {
    if (syncing.current) return;
    syncing.current = true;
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    syncing.current = false;
  }, []);

  // Track the table's scrollWidth so the top scrollbar spacer matches
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setScrollWidth(el.scrollWidth);
    });
    observer.observe(el);
    // Also observe the table inside it (column count changes)
    const table = el.querySelector("table");
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [records, visibleFields]);

  if (loading || permsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading table...</div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to view this table. Ask your administrator to grant you access."
        backTo="/portal/myapp/tables"
        backLabel="Back to Tables"
      />
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-destructive">{error}</div>
        <Button onClick={fetchForm}>Retry</Button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">Table not found</div>
        <Link to="/portal/myapp/tables" search={{}}>
          <Button>Back to Tables</Button>
        </Link>
      </div>
    );
  }

  // If form is not published, redirect to edit page
  if (!form.is_published) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">
          This table has not been published yet.
        </div>
        <Link to="/portal/myapp/$tableId/edit" params={{ tableId: String(formId) }}>
          <Button>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Table
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/portal/myapp/tables" search={{}}>
            <Button variant="ghost" size="icon" className="shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <h1 className="text-2xl md:text-4xl font-bold text-slate-900">{form.name}</h1>
              <Badge variant="info">Published</Badge>
              {form.table_type === "virtual" && (
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">Virtual</Badge>
              )}
              {permissions && !permissions.can_edit && permissions.can_view && (
                <Badge variant="outline" className="border-amber-300 text-amber-700">Read-only</Badge>
              )}
              {form.is_locked && (
                <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">Locked</Badge>
              )}
            </div>
            {form.description && (
              <p className="text-muted-foreground">{form.description}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="icon" onClick={fetchRecords}>
            <RefreshCw
              className={`h-4 w-4 ${recordsLoading ? "animate-spin" : ""}`}
            />
          </Button>
          {form.table_type !== "virtual" && permissions?.can_submit && (
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              disabled={form.is_locked}
              title={form.is_locked ? "Table is locked" : undefined}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
          )}
          {form.table_type === "virtual" && (
            <Button
              variant="outline"
              onClick={handleVirtualRefresh}
              disabled={virtualRefreshing}
            >
              {virtualRefreshing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              {virtualRefreshing ? "Refreshing..." : "Refresh Data"}
            </Button>
          )}
          <ExportToExcelButton
            onClick={handleExportClick}
            isExporting={exporting}
            disabled={total === 0}
            label="Export"
          />
          {form.table_type === "latest" && permissions?.can_archive && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowRestoreArchiveDialog(true)}
                title="Swap current data with the previously archived copy"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore Previous
              </Button>
              <Button
                variant="outline"
                className="text-destructive border-destructive/50 hover:bg-destructive/10"
                onClick={() => setShowPurgeArchiveDialog(true)}
                title="Permanently delete all archived rows"
              >
                <Archive className="h-4 w-4 mr-2" />
                Purge Archived
              </Button>
            </>
          )}
          {canAddRecords(form.table_type) && permissions?.can_submit && (
            form.needs_republish ? (
              <Button disabled title="Table design has been changed — contact your administrator to re-publish">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            ) : form.is_locked ? (
              <Button disabled title="Table is locked">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            ) : (
              <Link
                to="/portal/myapp/$tableId/submit"
                params={{ tableId: String(formId) }}
                // `from: "table"` flags this as in-app navigation so the
                // post-create handler lands on the new record in view mode
                // instead of falling back to the success card / grid bounce.
                search={{ from: "table" }}
              >
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Record
                </Button>
              </Link>
            )
          )}
          {!form.is_header_table && canAddRecords(form.table_type) && permissions?.can_submit && (
            form.needs_republish || form.is_locked ? (
              <Button variant="outline" disabled title={form.is_locked ? "Table is locked" : "Table design has been changed — contact your administrator to re-publish"}>
                <Grid className="h-4 w-4 mr-2" />
                Grid Entry
              </Button>
            ) : (
              <Link to="/portal/myapp/$tableId/grid" params={{ tableId: String(formId) }} search={{ mode: "create", ids: "" }}>
                <Button variant="outline">
                  <Grid className="h-4 w-4 mr-2" />
                  Grid Entry
                </Button>
              </Link>
            )
          )}
          {/* Grid Edit lives next to Grid Entry — always visible so the
              feature is discoverable, disabled with a tooltip until the
              user has ticked one or more rows. Mirrors Grid Entry's
              gating (non-header, non-virtual, can_edit, not locked). */}
          {!form.is_header_table && form.table_type !== "virtual" && permissions?.can_edit && (
            form.is_locked ? (
              <Button variant="outline" disabled title="Table is locked">
                <Grid className="h-4 w-4 mr-2" />
                Grid Edit
              </Button>
            ) : selectedIds.size === 0 ? (
              <Button variant="outline" disabled title="Tick one or more rows below, then click Grid Edit to bulk-edit them">
                <Grid className="h-4 w-4 mr-2" />
                Grid Edit
              </Button>
            ) : selectedIds.size > 500 ? (
              <Button variant="outline" disabled title="Grid Edit is capped at 500 rows per batch">
                <Grid className="h-4 w-4 mr-2" />
                Grid Edit ({selectedIds.size})
              </Button>
            ) : (
              <Link
                to="/portal/myapp/$tableId/grid"
                params={{ tableId: String(formId) }}
                search={{ mode: "edit", ids: Array.from(selectedIds).join(",") }}
              >
                <Button variant="outline">
                  <Grid className="h-4 w-4 mr-2" />
                  Grid Edit ({selectedIds.size})
                </Button>
              </Link>
            )
          )}
          <Link
            to={"/portal/myapp/$tableId/report" as "/portal/myapp/$tableId"}
            params={{ tableId: String(formId) }}
            target="_blank"
          >
            <Button variant="outline">
              <BarChart3 className="h-4 w-4 mr-2" />
              Report
            </Button>
          </Link>
          {isPlatformAdmin && (
            <Link
              to={"/portal/myapp/$tableId/kanban" as "/portal/myapp/$tableId"}
              params={{ tableId: String(formId) }}
            >
              <Button variant="outline">
                <LayoutGrid className="h-4 w-4 mr-2" />
                Kanban
              </Button>
            </Link>
          )}
          <Link to="/portal/myapp/$tableId/edit" params={{ tableId: String(formId) }}>
            <Button variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              View Structure
            </Button>
          </Link>
          {form.table_type !== "virtual" && permissions?.is_owner && (
            <Button
              variant="outline"
              className="text-destructive border-destructive/50 hover:bg-destructive/10"
              disabled={checkingPurge}
              onClick={async () => {
                setCheckingPurge(true);
                try {
                  const check = await myappApi.records.checkPurge(token, formId);
                  if (!check.can_purge) {
                    setPurgeBlockedRefs(check.referencing_tables);
                    setShowPurgeDialog(true);
                    return;
                  }
                  setPurgeBlockedRefs([]);
                  setShowPurgeDialog(true);
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Failed to check purge eligibility");
                } finally {
                  setCheckingPurge(false);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {checkingPurge ? "Checking..." : "Purge Data"}
            </Button>
          )}
        </div>
      </div>

      {/* Needs Re-publish Banner */}
      {form.needs_republish && (
        <div className="flex items-center gap-2 p-4 mb-6 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>
            The table design has been changed. Please contact your administrator to re-publish the table before adding or editing records.
          </span>
        </div>
      )}

      {/* Table Info */}
      <Card className="mb-6">
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">
                Table:{" "}
                <code className="bg-muted px-2 py-1 rounded text-sm">
                  {form.table_name}
                </code>
              </CardTitle>
              <CardDescription>
                {total} record{total !== 1 ? "s" : ""} • {visibleFields.length}{" "}
                visible field{visibleFields.length !== 1 ? "s" : ""}
                {form.table_type === "virtual" && (
                  <span className="ml-2">
                    • Read-only — data sourced from query
                  </span>
                )}
              </CardDescription>
            </div>
            {form.table_type === "virtual" && (
              virtualRefreshing ? (
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>
                    {virtualRefreshStatus === "queued" ? "Queued — waiting for worker..." : "Fetching data from source..."}
                  </span>
                </div>
              ) : form.last_refresh_at_utc ? (
                <div className="text-xs text-muted-foreground text-right space-y-0.5">
                  <div>
                    Last refresh:{" "}
                    {new Date(form.last_refresh_at_utc).toLocaleString()}
                  </div>
                  {form.last_refresh_row_count != null && (
                    <div>{form.last_refresh_row_count} rows refreshed</div>
                  )}
                  {form.last_refresh_status === "failed" && form.last_refresh_error && (
                    <div className="text-destructive">{form.last_refresh_error}</div>
                  )}
                </div>
              ) : null
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Version management panels (versioned tables only) */}
      {form.table_type === "versioned" && (
        <div className="mb-4">
          {(form.version_mode === "version_only" || form.version_mode === "version_and_milestone") ? (
            <VersionMilestonePanel
              tableId={formId}
              showMilestones={form.version_mode === "version_and_milestone"}
              onVersionsChanged={fetchRecords}
            />
          ) : (
            <ImportVersionsPanel
              tableId={formId}
              onVersionDeleted={fetchRecords}
            />
          )}
        </div>
      )}

      {/* Workflow-state filter chips. Rendered only when the table has
          states defined. Filter by the stable SysCurrentStateId (UUID),
          NOT the denormalised SysCurrentStateName. The latter is a
          snapshot that can drift when a state is renamed and the
          propagation either hasn't run or failed
          (state_service._propagate_state_name_to_data_rows is
          best-effort and degrades to a warning log on failure).
          Filtering by ID always matches the right records regardless
          of any displayed-name drift. See Theme 16 in the
          field-system-improvements roadmap. */}
      {hasStateMachine && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-muted-foreground">State:</span>
          <button
            type="button"
            onClick={() => {
              setStateFilter(null);
              updateColumnFilter("SysCurrentStateId", { value: "" });
            }}
            className={`rounded-full border px-3 py-0.5 text-xs ${
              stateFilter === null
                ? "bg-foreground text-background"
                : "bg-background"
            }`}
          >
            All
          </button>
          {tableStates.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setStateFilter(s.id);
                updateColumnFilter("SysCurrentStateId", { value: s.id });
              }}
              className={`rounded-full border px-3 py-0.5 text-xs ${
                stateFilter === s.id
                  ? "bg-foreground text-background"
                  : "bg-background"
              }`}
              style={
                stateFilter === s.id && s.colour_hex
                  ? { backgroundColor: s.colour_hex, borderColor: s.colour_hex, color: "#fff" }
                  : undefined
              }
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Filter & Selection Bar */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search all records..."
            value={filterText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {filterText && (
            <button
              onClick={() => { setFilterText(""); setDebouncedSearch(""); setOffset(0); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Column Filters
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
        {/* SysStatus visibility toggles */}
        {(form.table_type === "versioned" || form.table_type === "latest") && (
          <Button
            variant={includeArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setIncludeArchived(!includeArchived)}
            title={includeArchived ? "Hide archived rows" : "Show archived rows"}
          >
            <Archive className="h-4 w-4 mr-1" />
            Show Archived
          </Button>
        )}
        {selectedIds.size > 0 && form.table_type !== "virtual" && permissions?.can_delete && !form.is_locked && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">
              {selectedIds.size} selected
            </span>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowBulkDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        )}
        {debouncedSearch && (
          <span className="text-sm text-muted-foreground">
            {total} result{total !== 1 ? "s" : ""} for &ldquo;{debouncedSearch}&rdquo;
          </span>
        )}
      </div>

      {/* Data Grid */}
      <Card className="relative overflow-hidden">
        {/* Refresh progress bar */}
        {virtualRefreshing && (
          <div className="absolute top-0 left-0 right-0 z-10">
            <div className="h-0.5 bg-blue-100 w-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-shimmer w-1/3" />
            </div>
          </div>
        )}
        <CardContent className="p-0">
          {/* Top horizontal scrollbar mirror */}
          {scrollWidth > 0 && (
            <div
              ref={topScrollRef}
              className="overflow-x-auto border-b"
              onScroll={syncScrollFromTop}
              style={{ height: 12 }}
            >
              <div style={{ width: scrollWidth, height: 1 }} />
            </div>
          )}
          <div ref={tableScrollRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]" onScroll={syncScrollFromTable}>
            <Table>
              <TableHeader>
                <TableRow>
                  {form.table_type !== "virtual" && (
                    <TableHead className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={filteredRecords.length > 0 && selectedIds.size === filteredRecords.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead
                    className="w-[80px] cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort(form.primary_key_column)}
                  >
                    <div className="flex items-center gap-1">
                      {form.fields.find((f) => f.column_name === form.primary_key_column)?.name ?? form.primary_key_column}
                      {sortColumn === form.primary_key_column ? (
                        sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                  </TableHead>
                  {showFilesColumn && (
                    <TableHead className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        Files
                        {filesColumnError && <FilesErrorMarker message={filesColumnError} />}
                      </span>
                    </TableHead>
                  )}
                  {visibleFields.map((field) => (
                    <ResizableHeader
                      key={field.id}
                      fieldId={String(field.id)}
                      width={columnWidths[String(field.id)]}
                      onResize={handleColumnResize}
                      onClick={() => handleSort(field.column_name)}
                    >
                      <div className="flex items-center gap-1 pr-3">
                        {field.name}
                        {/* A Files field whose binding call failed (no
                            binding to use): the counts still show, as plain
                            text, but the files cannot be opened — the same
                            marker as the phase-1 Files column. */}
                        {field.field_type?.code === "connector_files" && connectorBindingFailed && (
                          <FilesErrorMarker message={FILES_BINDING_ERROR} />
                        )}
                        {sortColumn === field.column_name ? (
                          sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                    </ResizableHeader>
                  ))}
                  {systemColumns.map((sc) => (
                    <TableHead key={sc.key} className="text-xs text-muted-foreground whitespace-nowrap">
                      {sc.label}
                    </TableHead>
                  ))}
                  {form.table_type !== "virtual" && (
                    <TableHead className="w-[100px] text-right sticky right-0 bg-background z-10 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                      Actions
                    </TableHead>
                  )}
                </TableRow>
                {/* Column Filter Row */}
                {showFilters && (
                  <TableRow className="bg-muted/30">
                    {form.table_type !== "virtual" && <TableHead />}
                    <TableHead />
                    {showFilesColumn && <TableHead />}
                    {visibleFields.map((field) => {
                      const code = effectiveFieldTypeCode(field) || "text";
                      const currentFilter = columnFilters[field.column_name];

                      // A file count: sortable, not filterable.
                      if (code === "connector_files") {
                        return <TableHead key={field.id} />;
                      }

                      if (code === "bit") {
                        return (
                          <TableHead key={field.id} className="p-1">
                            <select
                              value={currentFilter?.value || ""}
                              onChange={(e) => updateColumnFilter(field.column_name, { value: e.target.value })}
                              className="w-full px-2 py-1 text-xs border rounded bg-background"
                            >
                              <option value="">All</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </TableHead>
                        );
                      }

                      if (["int", "bigint", "decimal", "money", "percentage", "calculation"].includes(code)) {
                        return (
                          <TableHead key={field.id} className="p-1">
                            <div className="flex gap-1">
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="Min"
                                value={currentFilter?.value || ""}
                                onChange={(e) => updateColumnFilter(field.column_name, { value: e.target.value })}
                                className="w-1/2 px-1.5 py-1 text-xs border rounded bg-background"
                              />
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="Max"
                                value={currentFilter?.value2 || ""}
                                onChange={(e) => updateColumnFilter(field.column_name, { value2: e.target.value })}
                                className="w-1/2 px-1.5 py-1 text-xs border rounded bg-background"
                              />
                            </div>
                          </TableHead>
                        );
                      }

                      if (["date", "datetime"].includes(code)) {
                        return (
                          <TableHead key={field.id} className="p-1">
                            <div className="space-y-1">
                              <input
                                type="date"
                                title="From date"
                                value={currentFilter?.value || ""}
                                onChange={(e) => updateColumnFilter(field.column_name, { value: e.target.value })}
                                className="w-full px-1.5 py-0.5 text-xs border rounded bg-background"
                              />
                              <input
                                type="date"
                                title="To date"
                                value={currentFilter?.value2 || ""}
                                onChange={(e) => updateColumnFilter(field.column_name, { value2: e.target.value })}
                                className="w-full px-1.5 py-0.5 text-xs border rounded bg-background"
                              />
                            </div>
                          </TableHead>
                        );
                      }

                      // Text-based filter (text, longtext, email, concatenation, link, lookup)
                      return (
                        <TableHead key={field.id} className="p-1">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Filter..."
                              value={currentFilter?.value || ""}
                              onChange={(e) => updateColumnFilter(field.column_name, { value: e.target.value })}
                              className="w-full px-2 py-1 text-xs border rounded bg-background pr-6"
                            />
                            {currentFilter?.value && (
                              <button
                                onClick={() => updateColumnFilter(field.column_name, { value: "" })}
                                className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                    {systemColumns.map((sc) => {
                      const currentFilter = columnFilters[sc.key];
                      return (
                        <TableHead key={sc.key} className="p-1">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Filter..."
                              value={currentFilter?.value || ""}
                              onChange={(e) => updateColumnFilter(sc.key, { value: e.target.value })}
                              className="w-full px-2 py-1 text-xs border rounded bg-background pr-6"
                            />
                            {currentFilter?.value && (
                              <button
                                onClick={() => updateColumnFilter(sc.key, { value: "" })}
                                className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                    {form.table_type !== "virtual" && (
                      <TableHead className="sticky right-0 bg-muted/30 z-10" />
                    )}
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {recordsLoading && records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleFields.length + systemColumns.length + 3 + (showFilesColumn ? 1 : 0)}
                      className="text-center py-8 text-muted-foreground"
                    >
                      Loading records...
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleFields.length + systemColumns.length + 3 + (showFilesColumn ? 1 : 0)}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No records yet.{" "}
                      <Link
                        to="/portal/myapp/$tableId/submit"
                        params={{ tableId: String(formId) }}
                        search={{}}
                        className="text-primary hover:underline"
                      >
                        Add your first record
                      </Link>
                    </TableCell>
                  </TableRow>
                ) : filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleFields.length + systemColumns.length + 3 + (showFilesColumn ? 1 : 0)}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No records match your filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record, rowIdx) => {
                    const pk = getPkValue(record);
                    const rawPk = getRawPkValue(record);
                    const sysStatus = (record as Record<string, unknown>).SysStatus as string | undefined;
                    const isArchived = sysStatus === "Archived";
                    const isDeleted = sysStatus === "Deleted";
                    // Per-row scope permissions from backend (Phase 3a).
                    // Absent when the table has no scope modes enabled, or for
                    // admin callers — default to true so the table-level flag
                    // decides.
                    const rowPerms = (record as Record<string, unknown>)._permissions as
                      | { can_edit?: boolean; can_delete?: boolean }
                      | undefined;
                    const rowCanEdit =
                      (permissions?.can_edit ?? false) && (rowPerms?.can_edit ?? true);
                    const rowCanDelete =
                      (permissions?.can_delete ?? false) && (rowPerms?.can_delete ?? true);
                    const sourceKey = getSourceKey(record);
                    const fileCount = sourceKey !== null ? (connectorCounts?.[sourceKey] ?? 0) : 0;
                    // A virtual row opens the record read-only (submit.tsx
                    // forces view mode on a virtual table); any other row
                    // opens it for an editor, as before.
                    const isVirtual = form.table_type === "virtual";
                    const rowOpens = isVirtual ? !!permissions?.can_view : rowCanEdit;
                    return (
                      <TableRow
                        key={rawPk ?? rowIdx}
                        className={`${rowOpens ? "cursor-pointer" : ""} hover:bg-muted/50 ${pk !== null && selectedIds.has(pk) ? "bg-muted/30" : ""} ${isArchived ? "opacity-60" : ""} ${isDeleted ? "opacity-40 line-through" : ""}`}
                        onClick={() =>
                          rowOpens && pk && navigate({ to: "/portal/myapp/$tableId/submit", params: { tableId: String(formId) }, search: { edit: pk } })
                        }
                      >
                        {form.table_type !== "virtual" && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={pk !== null && selectedIds.has(pk)}
                              onCheckedChange={() => pk && toggleRowSelect(pk)}
                              aria-label={`Select record ${pk}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm">
                          <span className="flex items-center gap-1.5">
                            {rawPk}
                            {isArchived && <Badge variant="outline" className="text-[10px] px-1 py-0 text-amber-600 border-amber-300">Archived</Badge>}
                            {isDeleted && <Badge variant="outline" className="text-[10px] px-1 py-0 text-destructive border-destructive/30">Deleted</Badge>}
                          </span>
                        </TableCell>
                        {showFilesColumn && (
                          <TableCell>
                            {/* Only a row KNOWN to have files gets the
                                popover (unknown while counts load or after
                                they fail). Keyed on the source key: the
                                popover caches its list for its lifetime, so a
                                reused cell must never show another row's.
                                The span stops clicks on the trigger and on
                                the popover's file buttons (portalled, but
                                bubbling through React) reaching the row. */}
                            {sourceKey !== null && fileCount > 0 && (
                              <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                                <AttachmentListPopover
                                  key={sourceKey}
                                  count={fileCount}
                                  source={connectorAttachmentSource(sourceKey)}
                                />
                              </span>
                            )}
                          </TableCell>
                        )}
                        {visibleFields.map((field) => {
                          const fieldValue = getFieldValue(record, field);
                          if (field.field_type?.code === "connector_files") {
                            return (
                              <TableCell key={field.id}>
                                <ConnectorFilesCell
                                  value={fieldValue}
                                  sourceKey={sourceKey}
                                  getSource={connectorAttachmentSource}
                                  bindingPending={connectorBindingPending}
                                />
                              </TableCell>
                            );
                          }
                          const displayValue = formatCellValue(fieldValue, field);
                          const isTextType = ["text", "longtext", "concatenation"].includes(field.field_type?.code || "");
                          const isSignature = field.field_type?.code === "signature";
                          return (
                            <TableCell key={field.id}>
                              {isSignature ? (
                                fieldValue && Number(fieldValue) > 0 ? (
                                  <span className="text-xs font-medium">Signed</span>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">&mdash;</span>
                                )
                              ) : isTextType ? (
                                <ExpandableText text={displayValue} />
                              ) : (
                                displayValue
                              )}
                            </TableCell>
                          );
                        })}
                        {systemColumns.map((sc) => {
                          // Render the State column as a coloured chip. Falls
                          // back to the initial state when the row's value is
                          // NULL — records pre-dating the state-machine config
                          // are conceptually in the initial state until they
                          // get backfilled.
                          if (sc.key === "SysCurrentStateName") {
                            // Match by the stable SysCurrentStateId (UUID) so a
                            // renamed state still shows the LIVE name + correct
                            // colour, even when the row's denormalised
                            // SysCurrentStateName is from before the rename.
                            // See Theme 16 in the field-system-improvements
                            // roadmap. Fallback to the legacy
                            // match-by-stored-name path when the row doesn't
                            // carry SysCurrentStateId (older records or
                            // payloads where the column was filtered out).
                            const stateId = (record as Record<string, unknown>)[
                              "SysCurrentStateId"
                            ] as string | undefined | null;
                            const storedName = (record as Record<string, unknown>)[
                              "SysCurrentStateName"
                            ] as string | undefined | null;
                            const fallback = tableStates.find((s) => s.is_initial);
                            const matched = stateId
                              ? tableStates.find((s) => s.id === stateId)
                              : storedName
                              ? tableStates.find((s) => s.name === storedName)
                              : undefined;
                            // Display priority: live name from the matched
                            // state (canonical, can't drift), then the
                            // stored snapshot, then the initial-state
                            // fallback for rows that pre-date the state
                            // machine.
                            const effectiveName =
                              matched?.name ?? storedName ?? fallback?.name;
                            return (
                              <TableCell key={sc.key} className="whitespace-nowrap">
                                {effectiveName ? (
                                  <StateBadge
                                    state={{
                                      name: effectiveName,
                                      colour_hex: matched?.colour_hex ?? null,
                                      is_terminal: matched?.is_terminal ?? false,
                                      description: matched?.description ?? null,
                                    }}
                                  />
                                ) : (
                                  ""
                                )}
                              </TableCell>
                            );
                          }
                          // Date system columns get formatted; everything else
                          // renders the raw value.
                          if (sc.key === "SysCreatedAtUtc" || sc.key === "SysUpdatedAtUtc") {
                            const raw = (record as Record<string, unknown>)[sc.key] as
                              | string
                              | null
                              | undefined;
                            return (
                              <TableCell key={sc.key} className="text-xs text-muted-foreground whitespace-nowrap">
                                {raw
                                  ? new Date(raw).toLocaleString(undefined, {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "—"}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={sc.key} className="text-xs text-muted-foreground whitespace-nowrap">
                              {(record as Record<string, unknown>)[sc.key] as string ?? ""}
                            </TableCell>
                          );
                        })}
                        {form.table_type !== "virtual" && (
                          <TableCell className="text-right sticky right-0 z-10 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                            <div className="flex justify-end gap-1">
                              {rowCanEdit && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={form.is_locked}
                                  title={form.is_locked ? "Table is locked" : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pk &&
                                      navigate({
                                        to: "/portal/myapp/$tableId/submit",
                                        params: { tableId: String(formId) },
                                        search: { edit: pk, mode: "edit" },
                                      });
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {rowCanEdit && form.is_header_table && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={form.is_locked}
                                  title={
                                    form.is_locked
                                      ? "Table is locked"
                                      : "Clone record (opens as unsaved draft)"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pk &&
                                      navigate({
                                        to: "/portal/myapp/$tableId/submit",
                                        params: { tableId: String(formId) },
                                        search: { cloneFrom: pk },
                                      });
                                  }}
                                  aria-label="Clone record"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              )}
                              {rowCanDelete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={form.is_locked}
                                  title={form.is_locked ? "Table is locked" : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pk && setDeleteId(pk);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="px-4 py-3 border-t">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={total}
                pageSize={limit}
                pageSizeOptions={[20, 50, 100]}
                startIndex={offset + 1}
                endIndex={Math.min(offset + limit, total)}
                canGoPrev={currentPage > 1}
                canGoNext={currentPage < totalPages}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onFirstPage={() => goToPage(1)}
                onLastPage={() => goToPage(totalPages)}
                onPrevPage={() => goToPage(currentPage - 1)}
                onNextPage={() => goToPage(currentPage + 1)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Record</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {form?.is_header_table
                    ? "This will mark this record and all its line items as deleted."
                    : "This will mark this record as deleted. It can be restored later by an administrator."}
                </p>
                {checkingRefs && (
                  <p className="text-sm text-muted-foreground">Checking references...</p>
                )}
                {deleteRefs.length > 0 && (
                  <div className="p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                      This record is referenced by other tables:
                    </p>
                    <ul className="list-disc list-inside text-amber-700 dark:text-amber-300">
                      {deleteRefs.map((ref) => (
                        <li key={ref.table_name}>
                          {ref.record_count} row{ref.record_count !== 1 ? "s" : ""} in{" "}
                          <strong>{ref.table_name}</strong>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Those records will still reference this record, but it will no longer
                      appear in dropdown lists for new entries.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting || checkingRefs}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Options Dialog */}
      <ExportOptionsDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExportToExcel}
        hasArchivedData={form?.table_type === "versioned" || form?.table_type === "latest"}
        childTableCount={childFormCount}
        isExporting={exporting}
        /* Only pass filteredRowCount when the grid actually has an
           active filter / search. The dialog suppresses the filter
           panel entirely when filteredRowCount is undefined, keeping
           the no-filter happy path visually unchanged. */
        filteredRowCount={hasActiveFilters || debouncedSearch ? total : undefined}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={showImportDialog}
        onOpenChange={handleImportDialogChange}
        tableId={formId}
        fields={form.fields}
        tableType={form.table_type === "bridge" ? "simple" : form.table_type || "simple"}
        versionMode={form.version_mode || "filename"}
        areaMode={form.area_mode || "none"}
        areaSetId={form.area_set_id}
        defaultSourceType={form.source_type}
        defaultSourceName={form.source_name}
        onImportComplete={fetchRecords}
        initialJobId={pendingResumeJobId}
        userCanDelete={permissions?.can_delete ?? false}
        autoNumberTargetFieldId={form.auto_number_target_field_id}
      />

      {/* Restore Archive Dialog (latest tables) */}
      <AlertDialog open={showRestoreArchiveDialog} onOpenChange={setShowRestoreArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Previous Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will swap the current data with the previously archived data.
              The current data will become the archived copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveActioning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreArchive}
              disabled={archiveActioning}
            >
              {archiveActioning ? "Restoring..." : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Purge Archive Dialog (latest tables) */}
      <AlertDialog open={showPurgeArchiveDialog} onOpenChange={setShowPurgeArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Purge Archived Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all archived rows from this table.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveActioning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurgeArchive}
              disabled={archiveActioning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {archiveActioning ? "Purging..." : "Purge"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} Record{selectedIds.size !== 1 ? "s" : ""}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the selected records as deleted. They can be restored later by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Purge All Data Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={(open) => {
        setShowPurgeDialog(open);
        if (!open) {
          setPurgeConfirmText("");
          setPurgeBlockedRefs([]);
        }
      }}>
        <DialogContent>
          {purgeBlockedRefs.length > 0 ? (
            <>
              <DialogHeader>
                <DialogTitle>Cannot Purge Data</DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3">
                    <p>The following tables reference this table and must be purged first:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      {purgeBlockedRefs.map((ref) => (
                        <li key={ref.table_name} className="text-sm">
                          <span className="font-mono">{ref.table_name}</span> ({ref.record_count} records)
                        </li>
                      ))}
                    </ul>
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPurgeDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Purge All Data?</DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3">
                    <p>This will permanently delete all records in this table and cascade to child tables.</p>
                    <p className="font-semibold">Type <code className="px-1 py-0.5 rounded bg-muted text-foreground">{form?.table_name}</code> to confirm:</p>
                    <Input
                      value={purgeConfirmText}
                      onChange={(e) => setPurgeConfirmText(e.target.value)}
                      placeholder={`Type "${form?.table_name}" to confirm`}
                      autoFocus
                    />
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowPurgeDialog(false); setPurgeConfirmText(""); }} disabled={purging}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handlePurge}
                  disabled={purging || purgeConfirmText !== form?.table_name}
                >
                  {purging ? "Purging..." : "Purge All Data"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
